import { useKeycloak } from '../../features/auth/components/KeycloakProvider';
import { login as keycloakLogin } from '@features/auth/services/keycloakService';
import { FullPageLoader } from '@shared/components/LoadingSpinner';
import { useEffect } from 'react';

/**
 * Protected route wrapper. Uses Keycloak's authentication state as the source of truth.
 * Waits for Keycloak initialization before allowing access to protected routes.
 * This prevents stale tokens from localStorage from granting access to expired sessions.
 */
export function ProtectedRoute({ children }) {
  const { keycloak, initialized, idpHint } = useKeycloak();

  useEffect(() => {
    // If Keycloak is initialized but user is not authenticated, trigger login. On the subdomain path
    // `idpHint` pins the tenant's Identity Provider; on the main domain it's null → generic login.
    // Delegated to keycloakService.login, which owns that null-idpHint rule (shared with LogoutPage).
    // The `keycloak &&` guard must stay: the service resolves the instance via getKeycloak(), which
    // throws when Keycloak never initialized.
    if (initialized && keycloak && !keycloak.authenticated) {
      keycloakLogin({ idpHint });
    }
  }, [initialized, keycloak, idpHint]);

  // Wait for Keycloak to fully initialize before rendering any protected content
  if (!initialized) {
    return <FullPageLoader />;
  }

  // Use Keycloak's authenticated status as the source of truth. Unauthenticated means a redirect
  // is under way (to sign-in above, or when the session ended mid-use), so keep a loader on screen
  // rather than an empty page.
  if (!keycloak?.authenticated) {
    return <FullPageLoader />;
  }

  return children;
}
